import api from './client';

// =============================================================================
// Check-in Template API (Spec 215)
//
// Backend contract (committed): /api/checkin-templates CRUD + fork, and
// /api/tasks/:taskId/checkin-responses for first-submission writes and
// audited reads. Responses pass through camelCaseResponse, which recurses
// into the `prompts` JSONB array — so `role_required` arrives as
// `roleRequired`. Prompt ids are kebab-case (`felt-good`), which the
// snake→camel transform leaves intact, so the responses map keyed by
// prompt id round-trips losslessly.
// =============================================================================

/** A single check-in question. 215-D2: prompts are a flat array. */
export interface CheckinPrompt {
  id: string;
  question: string;
  type: 'text-short' | 'text-long' | 'scale-1-10' | 'select';
  // Whether the question is shown only to the submissive, only to the
  // dominant, or to both (null = either role answers).
  roleRequired: 'submissive' | 'dominant' | null;
  optional: boolean;
}

export interface CheckinTemplate {
  id: string; // UUID PK (215-D2 — not SERIAL, unlike protocol_templates)
  organizationId: number | null; // raw DB id; do NOT compare to a public-id
  isSystem: boolean;
  parentTemplateId: string | null;
  slug: string | null;
  name: string;
  description: string | null;
  prompts: CheckinPrompt[];
  metadata: Record<string, unknown> | null;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
  // UUID public id of the owning org. Ownership checks use this
  // string-vs-string against Organization.id — never the numeric
  // `organizationId` (bug-registry "id-shape mismatch", 2026-05-18).
  organizationPublicId?: string | null;
}

/** Responses payload: { [promptId]: answer }. Scale answers are numbers. */
export type CheckinResponses = Record<string, string | number>;

export const checkinTemplateAPI = {
  // ===========================================================================
  // Templates
  // ===========================================================================

  /** System templates + the caller org's own (215-D3). */
  getTemplates: () =>
    api.get<{ data: CheckinTemplate[] }>('/checkin-templates'),

  /** Single template by UUID id. */
  getTemplate: (templateId: string) =>
    api.get<{ data: CheckinTemplate }>(`/checkin-templates/${templateId}`),

  /** Fork a template into an org-owned editable copy (admin / 215-D4). */
  forkTemplate: (templateId: string) =>
    api.post<{ data: CheckinTemplate }>(
      `/checkin-templates/${templateId}/fork`
    ),

  /** Update an org-owned template (creator or org admin — 215-D4). */
  updateTemplate: (
    templateId: string,
    data: {
      name?: string;
      description?: string | null;
      prompts?: CheckinPrompt[];
      metadata?: Record<string, unknown>;
    }
  ) =>
    api.put<{ data: CheckinTemplate }>(
      `/checkin-templates/${templateId}`,
      data
    ),

  /** Hard-delete an org-owned template (no soft-delete — 215-D2). */
  deleteTemplate: (templateId: string) =>
    api.delete<{ success: boolean }>(`/checkin-templates/${templateId}`),

  // ===========================================================================
  // Task check-in responses
  // ===========================================================================

  /**
   * First submission ONLY (215-D5). A second submit for a task that already
   * has responses returns 409 — there is no edit path. `taskId` is the task
   * public id (UUID string); the route resolves it server-side.
   */
  submitResponses: (taskId: string, responses: CheckinResponses) =>
    api.put<{ data: { submitted: true } }>(
      `/tasks/${taskId}/checkin-responses`,
      { responses }
    ),

  /**
   * Read a task's check-in responses. Visible ONLY to the responding
   * submissive or their active paired dominant (215-D1, enforced server-side);
   * every read writes an audit row. Returns null if nothing submitted yet.
   */
  getResponses: (taskId: string) =>
    api.get<{ data: CheckinResponses | null }>(
      `/tasks/${taskId}/checkin-responses`
    ),
};
